import type { ComponentType, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useFeature, useFlagReady } from '@hero/web-context';
import type { FeatureResult, SubFlagKeysOf } from '@hero/core';
import { heroLogoBlue } from '@hero/web-assets';
import { WrenchIcon, HourglassIcon } from '@hero/web-icons';
import { cn } from '@hero/web-lib';

type BlockKind = 'coming_soon' | 'maintenance' | 'disabled';

interface BlockedContentProps {
  kind: BlockKind;
  /** Optional override for the status icon. Defaults to a sensible icon per kind. */
  icon?: ComponentType<{ className?: string }>;
}

const KIND_BADGE_CLASSES: Record<BlockKind, string> = {
  coming_soon: 'bg-blue-100 text-blue-700',
  maintenance: 'bg-amber-100 text-amber-700',
  disabled: 'bg-gray-100 text-gray-600',
};

const KIND_ICON_WRAPPER_CLASSES: Record<BlockKind, string> = {
  coming_soon: 'bg-blue-50 text-blue-600',
  maintenance: 'bg-amber-50 text-amber-600',
  disabled: 'bg-gray-100 text-gray-500',
};

const DEFAULT_ICONS: Record<BlockKind, ComponentType<{ className?: string }>> = {
  coming_soon: HourglassIcon,
  maintenance: WrenchIcon,
  disabled: HourglassIcon,
};

const COPY_KEYS: Record<BlockKind, { badge: string; title: string; message: string }> = {
  coming_soon: {
    badge: 'c-main:app_gate.coming_soon_badge',
    title: 'c-main:app_gate.coming_soon_title',
    message: 'c-main:app_gate.coming_soon_message',
  },
  maintenance: {
    badge: 'c-main:app_gate.maintenance_badge',
    title: 'c-main:app_gate.maintenance_title',
    message: 'c-main:app_gate.maintenance_message',
  },
  disabled: {
    badge: 'c-main:app_gate.disabled_badge',
    title: 'c-main:app_gate.disabled_title',
    message: 'c-main:app_gate.disabled_message',
  },
};

/**
 * Stable outer shell shared by the hydrating placeholder and the block screen.
 * Renders the logo at the top, a content slot in the middle, and a footer
 * spacer at the bottom. As long as the gate keeps returning this component,
 * React preserves the same DOM nodes — so the logo `<img>` is mounted once
 * and never re-fetched, re-laid-out, or re-faded across the
 * hydrating → block transition.
 */
function LiveAppGateShell({ children }: { children?: ReactNode }) {
  const { t } = useTranslation(['c-main']);
  return (
    <div
      className="flex min-h-svh w-full flex-1 flex-col items-center justify-between bg-background p-8 text-foreground"
      role="status">
      <div className="flex w-full justify-center pt-4">
        <img
          src={heroLogoBlue}
          alt={t('c-main:app_gate.logo_alt')}
          className="h-10 w-auto"
          decoding="sync"
        />
      </div>

      <div className="flex w-full max-w-md flex-col items-center gap-4 text-center">
        {children}
      </div>

      <div className="h-10" aria-hidden />
    </div>
  );
}

function LiveAppBlockedContent({ kind, icon }: BlockedContentProps) {
  const { t } = useTranslation(['c-main']);
  const StatusIcon = icon ?? DEFAULT_ICONS[kind];
  const copy = COPY_KEYS[kind];

  return (
    // Fade only the inner content — the surrounding `LiveAppGateShell` (logo,
    // background, footer) is the same instance React used for the hydrating
    // state, so it stays solid while the icon/title/message cross-fade in.
    <div className="flex flex-col items-center gap-4 animate-in fade-in duration-300">
      <div className={cn('rounded-full p-6', KIND_ICON_WRAPPER_CLASSES[kind])}>
        <StatusIcon className="h-12 w-12" />
      </div>
      <span
        className={cn(
          'rounded-full px-3 py-1 text-xs font-medium',
          KIND_BADGE_CLASSES[kind],
        )}>
        {t(copy.badge)}
      </span>
      <h1 className="text-2xl font-semibold tracking-tight">{t(copy.title)}</h1>
      <p className="text-base text-muted-foreground">{t(copy.message)}</p>
    </div>
  );
}

function statusToBlockKind(
  status: FeatureResult['status'],
  isEnabled: boolean,
): BlockKind | null {
  if (status === 'coming_soon') return 'coming_soon';
  if (status === 'maintenance') return 'maintenance';
  if (status === 'disabled' || status === 'hidden' || !isEnabled) return 'disabled';
  return null;
}

export interface LiveAppSubGateProps {
  /** Which surface under `live_apps` this build targets. */
  subFlag: SubFlagKeysOf<'live_apps'>;
  children: ReactNode;
  /** Optional override for the status icon (defaults to Hourglass/Wrench per kind). */
  icon?: ComponentType<{ className?: string }>;
}

/**
 * Full-app gate for a `live_apps` sub-flag (dashboard surface).
 * Renders a **branded, app-level** unavailable screen (Hero logo + status icon
 * + app-wide copy) outside the normal app shell when the surface is off,
 * instead of the per-feature screens.
 *
 * Resolution is delegated to the unified `useFeature` hook, which:
 * - Uses the localStorage cache on warm paint.
 * - Treats "PostHog hydrated, flag absent" as `disabled` — i.e. toggling
 *   `live_apps` OFF at the project level is a true kill switch and cascades
 *   to every sub-flag automatically.
 *
 * Boot ordering: until PostHog has *any* decisive answer for `live_apps`
 * (`useFlagReady`) the gate renders {@link LiveAppGateShell} with no inner
 * content — just the logo on the background. Readiness short-circuits on
 * a cached value from a previous session, so warm paint renders the
 * dashboard (or block screen) immediately and only true cold paint sits
 * on the splash for the `/decide` round-trip. Once a decision is in, if
 * the flag resolves to a blocking state, the gate keeps the same shell and
 * slots the block content into it, so the logo doesn't unmount/refetch/flicker.
 */
export function LiveAppSubGate({ subFlag, children, icon }: LiveAppSubGateProps) {
  const ready = useFlagReady('live_apps');
  const { isEnabled, status } = useFeature('live_apps', subFlag);
  const blockKind = ready ? statusToBlockKind(status, isEnabled) : null;

  if (!ready || blockKind) {
    return (
      <LiveAppGateShell>
        {blockKind ? <LiveAppBlockedContent kind={blockKind} icon={icon} /> : null}
      </LiveAppGateShell>
    );
  }

  return <>{children}</>;
}
